import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import { config } from 'src/utils/config';
import { generatePkce, generateState, storePkceSession } from 'src/utils/pkce';
import styles from './AuthorizeBounce.module.scss';

const SCOPES = 'read_assets write_assets manage_collections manage_schedules send_notifications';

export const AuthorizeBounce: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  // Sent as `login_hint`: if the browser's FileSpin session is a different
  // account, the consent screen says so instead of authorizing that one.
  const userEmail: string | undefined = useAuthUser()()?.userEmail;

  const params = new URLSearchParams(location.search);
  const returnTo = params.get('return_to') || '/ai/schedules';

  useEffect(() => {
    let cancelled = false;

    async function startFlow() {
      try {
        const { verifier, challenge } = await generatePkce();
        const state = generateState();

        storePkceSession({ verifier, state, returnTo });

        const redirectUri = `${window.location.origin}/ai/schedules/oauth-callback`;
        const authUrl = new URL(`${config.hostUrl}/oauth/authorize`);
        authUrl.searchParams.set('client_id', config.aiAgentScheduleOauthClientId);
        authUrl.searchParams.set('response_type', 'code');
        authUrl.searchParams.set('scope', SCOPES);
        authUrl.searchParams.set('redirect_uri', redirectUri);
        authUrl.searchParams.set('code_challenge', challenge);
        authUrl.searchParams.set('code_challenge_method', 'S256');
        authUrl.searchParams.set('state', state);
        if (userEmail) authUrl.searchParams.set('login_hint', userEmail);

        if (!cancelled) {
          window.location.assign(authUrl.toString());
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to start authorization flow');
        }
      }
    }

    startFlow();
    return () => { cancelled = true; };
  }, [returnTo, userEmail]);

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h2>Authorization failed</h2>
          <p>{error}</p>
          <button className={styles.btn} onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.spinner} />
        <p>Redirecting to authorize…</p>
      </div>
    </div>
  );
};
